import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/utils/cn'

const textFieldVariants = cva(
  'group flex items-center gap-2 w-full px-3 text-body-md bor-2 hover:border-brand-primary transition-200 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60',
  {
    variants: {
      size: {
        md: 'h-8 px-3.5 rounded-2.5',
        lg: 'h-12 px-5 rounded-14px',
        lgx: 'h-12 px-26px rounded-14px text-body-lg',
      },
    },
    defaultVariants: {
      size: 'md'
    },
  },
)

const textFieldInputVariants = cva(
  'flex-1 bg-transparent text-primary placeholder:text-tertiary focus-visible:outline-none disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        md: 'text-body-md',
        lg: 'text-body-md',
        lgx: 'text-body-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    }
  }
)

export interface TextFieldRootProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof textFieldVariants> {
  className?: string
  inputClassName?: string
  centerIcon?: boolean
}

type TextFieldSlotSide = 'start' | 'end'

export interface TextFieldSlotProps extends React.HTMLAttributes<HTMLSpanElement> {
  asChild?: boolean
  side?: TextFieldSlotSide
}

const TextFieldSlot = React.forwardRef<HTMLSpanElement, TextFieldSlotProps>(
  ({ asChild = false, side = 'start', className, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'span'

    return (
      <Comp
        ref={ref}
        data-slot-side={side}
        className={cn(
          'flex items-center justify-center text-tertiary group-data-[disabled]:text-tertiary/60',
          className,
        )}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
TextFieldSlot.displayName = 'TextFieldSlot'

const TextFieldRoot = React.forwardRef<HTMLInputElement, TextFieldRootProps>(
  ({ className, inputClassName, children, disabled, type = 'text', size, centerIcon, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null)
    const startSlots: React.ReactNode[] = []
    const endSlots: React.ReactNode[] = []

    React.Children.forEach(children, child => {
      if (!React.isValidElement(child) || child.type !== TextFieldSlot) {
        return
      }

      const side: TextFieldSlotSide = child.props.side ?? 'start'
      if (side === 'end') {
        endSlots.push(child)
      } else {
        startSlots.push(child)
      }
    })

    // 合并 ref
    React.useImperativeHandle(ref, () => inputRef.current!, [])

    return (
      <div
        data-disabled={disabled ? '' : undefined}
        className={cn(
          'cursor-text',
          textFieldVariants({ size, className }),
          centerIcon && 'justify-center',
          className,
        )}
        onClick={() => {
          inputRef.current?.focus()
        }}
      >
        {startSlots}
        <input
          ref={inputRef}
          type={type}
          disabled={disabled}
          className={cn(
            textFieldInputVariants({ size, className: inputClassName }),
            centerIcon && 'flex-0 w-10ch focus:flex-1 transition-200 transition-flex',
          )}
          {...props}
        />
        {endSlots}
      </div>
    )
  },
)
TextFieldRoot.displayName = 'TextFieldRoot'

const TextField = {
  Root: TextFieldRoot,
  Slot: TextFieldSlot,
}

export { TextField, TextFieldRoot, TextFieldSlot }
